// Problem-intent article cluster — the real 2 a.m. searches, answered
// properly. Each article genuinely answers the query first, then quietly
// connects to the relevant stage/authority page. Rendered by the same
// SeoPage component as the authority pages (same data shape), under
// /sleep-guides/<slug>. No filler, no exaggerated promises, no medical
// claims — pediatrician-first where anything feels medical.

export const ARTICLES = {
  "newborn-day-night-confusion": {
    cluster: "Newborn",
    path: "/sleep-guides/newborn-day-night-confusion",
    meta: {
      title: "Newborn Day and Night Confusion | The Nurtured Parent Co.",
      description:
        "Newborn day/night confusion is real, common, and temporary. What's actually happening, what gently helps, and when to ask for support — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Newborn",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Newborn day/night confusion" },
    ],
    h1: ["Newborn day and night confusion:", "why it happens, what helps."],
    hero: [
      "Your newborn sleeps in long, deep stretches all day — and then midnight arrives and they're bright-eyed, ready to feed, ready to look around, ready for anything except sleep. If you're reading this at 3 a.m. with a wide-awake baby: you're not doing anything wrong, and your baby isn't broken.",
      "Day/night confusion is one of the most normal things a newborn can do. Here's what's actually going on — and what genuinely helps.",
    ],
    sections: [
      {
        h2: "What day and night confusion actually is",
        body: [
          "In the womb, your baby never had a day and a night. There was no light cycle, no melatonin rhythm of their own — just constant warmth, motion when you walked (which rocked them to sleep), and stillness when you rested (which often woke them up). Many parents notice their baby was most active at night before birth. That pattern doesn't switch off at delivery.",
          "A newborn's circadian rhythm — the internal clock that tells bodies when to be awake and when to sleep — takes weeks to organize after birth. In the early weeks, your baby borrows yours: some of your melatonin still reaches them, and it fades gradually. Until their own rhythm matures, sleep gets distributed around the clock in a way that can feel exactly backwards.",
        ],
      },
      {
        h2: "What normal newborn sleep actually looks like",
        body: [
          "Newborns sleep a lot — often fourteen to seventeen hours across the day — but almost never in long, organized stretches. Sleep arrives in pieces of two to four hours, scattered across day and night, because feeding drives the pattern more than any clock does.",
          "That means frequent waking at night is not the confusion — it's newborn sleep itself. The goal in these weeks is not an uninterrupted night; it's enough total sleep, full feeds, and a rhythm that slowly learns which end of the day is which.",
        ],
      },
      {
        h2: "Why nights end up the awake time",
        body: [
          "Day/night confusion is expected in the first weeks and usually resolves as the rhythm matures — often somewhere between six and twelve weeks. It's not a habit you created, and it's not a sign of what's coming.",
          "If the long awake stretches come with a baby who seems genuinely uncomfortable — arching, a cry that feels different, feeding struggles — that's a pediatrician conversation first, not a sleep one. Comfort always comes before scheduling.",
        ],
      },
      {
        h2: "Let the daylight do the teaching",
        body: [
          "The single strongest signal for a developing rhythm is light. Get your baby into natural daylight in the morning — a window, a walk, the porch with coffee. Keep days ordinary: normal household noise, conversation, faces, interaction. Don't tiptoe around daytime naps or darken the house for them.",
          "You're not forcing anything. You're simply making day feel like day, over and over, until their clock believes you.",
        ],
      },
      {
        h2: "Make nighttime boring",
        body: [
          "Nights get the opposite treatment: lights low, voices soft, movement slow. Night feeds stay calm and business-like — feed, burp, change only if needed, back down to sleep. Save the smiles and the play for the morning.",
          "The contrast is the whole lesson. Babies learn day from night by the difference between them, and you're the one building that difference.",
        ],
      },
      {
        h2: "Feeding comes first",
        body: [
          "Much of the night waking in these weeks is hunger, and it should be — newborns need to eat often, around the clock. If daytime stretches run very long, waking your baby to feed during the day can help calories cluster toward daylight hours; your pediatrician's guidance on feeding always leads, especially in the first weeks.",
          "What you won't find here: withholding feeds, keeping a newborn up to 'tire them out', or sleep training. An overtired or underfed newborn sleeps worse, not better — and newborns are too young for formal sleep training of any kind.",
        ],
      },
      {
        h2: "What the first weeks and months look like",
        body: [
          "Expect gradual, not linear. Most families see the nights slowly lengthen and the days slowly wake up somewhere between six and twelve weeks — with detours during growth spurts and wonder weeks. Some babies sort it out earlier, some later; both are normal.",
          "If days and nights stay flipped well past that window, or if you're simply too tired to wait it out, that's worth a conversation — gentle newborn foundations can support the rhythm without any sleep training at all.",
        ],
        links: [
          { href: "/newborn-sleep-consultant", label: "Newborn sleep support (0–3 months)" },
        ],
      },
      {
        h2: "One more thing about where they sleep",
        body: [
          "Day/night confusion often travels with a companion: a newborn who only sleeps on you. The two feed each other — contact naps all day, broken nights. Both are normal, and both respond to the same patience.",
        ],
        links: [
          { href: "/sleep-guides/newborn-only-sleeps-on-me", label: "Why your newborn only sleeps on you" },
        ],
      },
    ],
    faq: [
      {
        q: "Why does my newborn sleep all day and stay awake at night?",
        a: "Because they arrived with no internal clock — and often with an inverted one from the womb, where your daytime motion rocked them to sleep and your nighttime stillness woke them. Their circadian rhythm takes weeks to organize after birth. Day sleeping isn't the problem to fix; the fix is building contrast — bright, interactive days and dark, boring nights — until their rhythm catches on.",
      },
      {
        q: "How long does newborn day/night confusion last?",
        a: "For most babies it resolves somewhere between six and twelve weeks as the circadian rhythm matures. It's rarely a switch — more a slow drift toward longer night stretches and more wakeful days. If it's still firmly flipped past three months, or you're running on empty, individualized support can gently speed the process.",
      },
      {
        q: "Should I wake my newborn during the day?",
        a: "To feed, often yes — if a daytime stretch runs long, waking for a feed keeps calories coming and nudges the pattern toward daylight. Follow your pediatrician's lead on feeding frequency, especially early on. What you shouldn't do is keep a newborn awake to wear them out: an overtired newborn sleeps worse, not better.",
      },
      {
        q: "How can I help my newborn recognize nighttime?",
        a: "Make night feel unmistakably different from day: dark room, low voices, slow movements, calm and efficient feeds, diaper changes only when needed. No playing, no bright lights, no wide-awake engagement at 2 a.m. The boredom is the message — and babies read it within weeks.",
      },
      {
        q: "When does a baby's natural sleep rhythm develop?",
        a: "The circadian rhythm begins organizing in the first weeks and is usually meaningfully in place by around three to four months — which is also when longer night stretches become biologically possible. Before that, irregular sleep around the clock is developmentally normal, not a problem you've caused.",
      },
      {
        q: "Is day/night confusion a sign of a bigger sleep problem?",
        a: "Almost always, no — it's a normal developmental stage that resolves as your baby's internal clock matures. If something feels medically off (discomfort, feeding trouble), your pediatrician comes first.",
      },
    ],
    related: [
      { href: "/newborn-sleep-consultant", label: "Newborn sleep consultant" },
      { href: "/sleep-guides/newborn-only-sleeps-on-me", label: "Why your newborn only sleeps on you" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "newborn-only-sleeps-on-me": {
    cluster: "Newborn",
    path: "/sleep-guides/newborn-only-sleeps-on-me",
    meta: {
      title: "Why Your Newborn Only Sleeps on You (Contact Sleep) | The Nurtured Parent Co.",
      description:
        "Contact naps and a newborn who wakes the moment they're put down — why it happens, what's normal in the fourth trimester, and how to gently build toward the crib.",
    },
    label: "Sleep guides · Newborn",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Newborn contact sleep" },
    ],
    h1: ["Why your newborn", "only sleeps on you."],
    hero: [
      "Your baby sleeps beautifully — on you. On your chest, in your arms, against your shoulder. The moment they touch the crib, it's over. You've heard 'drowsy but awake' more times than you can count, and you're starting to wonder if you've already created a habit you can't undo.",
      "You haven't. What you're seeing is one of the most biologically normal things a newborn can do.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "Your newborn spent months sleeping to the sound of your heartbeat, the rhythm of your breathing, the motion of your walking. Your body is quite literally their known sleep environment. The crib — flat, still, quiet, spacious — is the unfamiliar one.",
          "This is the fourth trimester doing exactly what it evolved to do. Contact sleep isn't a bad habit; it's a starting point.",
        ],
      },
      {
        h2: "What's normal — and what to watch",
        body: [
          "Normal: strong preference for sleeping on you, waking at the transfer, short crib stretches at first. Very normal in the first three months.",
          "Worth a closer look: if you're contact sleeping out of desperation at night in ways that feel unsafe, that's a signal to change the arrangement — safely matters more than any sleep goal. A firm, flat, empty crib or bassinet is the safe sleep space; if nights involve dozing together, talk to your pediatrician about the safest possible setup.",
        ],
      },
      {
        h2: "How to gently build toward the crib",
        list: [
          "Start with one crib sleep a day — often the first nap, when sleep pressure is highest.",
          "Let them fall fully asleep before the transfer; limp and heavy moves better than drowsy.",
          "Warm the surface first, and keep your hands on them for a minute after the put-down.",
          "Expect short stretches at first. A 20-minute crib nap is practice, not failure.",
          "Keep contact naps in the mix without guilt — foundations beat force at this age.",
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If weeks are passing and the crib is still losing every time — or you're too tired to keep the transfer attempts going — that's exactly what newborn support is for. Gentle, staged, built around your baby's pace.",
        ],
        links: [
          { href: "/newborn-sleep-consultant", label: "Newborn sleep support (0–3 months)" },
        ],
      },
    ],
    faq: [
      {
        q: "Are contact naps creating a bad habit?",
        a: "Not in the newborn months. You're meeting a biological need while gently building crib tolerance alongside it. Habits are a later conversation — and a much easier one than the internet suggests.",
      },
      {
        q: "When should my newborn be sleeping in the crib?",
        a: "There's no deadline. Most families gradually shift more sleep to the crib across the first three months. If it isn't shifting at all and you want it to, that's something we can work on together.",
      },
    ],
    related: [
      { href: "/newborn-sleep-consultant", label: "Newborn sleep consultant" },
      { href: "/sleep-guides/newborn-day-night-confusion", label: "Newborn day/night confusion" },
      { href: "/sleep-guides/baby-wakes-when-put-down", label: "Baby wakes the moment you put them down" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "baby-waking-every-2-hours": {
    cluster: "Baby & Infant",
    path: "/sleep-guides/baby-waking-every-2-hours",
    meta: {
      title: "Why Your Baby Is Waking Every 2 Hours | The Nurtured Parent Co.",
      description:
        "Frequent night waking in babies 4–17 months: what waking every 2 hours usually means, the patterns behind it, and what actually helps — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Baby & infant",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Waking every 2 hours" },
    ],
    h1: ["Why your baby is waking", "every two hours."],
    hero: [
      "The night has stopped having a shape. Your baby goes down fine — and then it's every two hours, sometimes every ninety minutes, and by morning you can't tell where the wakings ended and the day began.",
      "Frequent night waking is the most common thing families bring to me. It's also almost never one thing. Here's how to start reading it.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "Everyone cycles through lighter and deeper sleep all night — adults too. We just learned long ago how to link those cycles without fully waking. A baby who wakes every cycle isn't necessarily in a crisis; they're surfacing between cycles and needing the same conditions that got them to sleep at bedtime to get back.",
          "That's why the most useful question usually isn't 'why are they waking?' — it's 'how are they falling asleep?'",
        ],
      },
      {
        h2: "The usual suspects",
        list: [
          "Sleep onset association: rocked, fed, or held to sleep at bedtime — and needed again at every cycle.",
          "A schedule mismatch: too much daytime sleep, too little, or a bedtime that doesn't match their real rhythm.",
          "Overtiredness: paradoxically, the later and harder the day ran, the more fragmented the night.",
          "Genuine hunger — especially in younger infants, growth spurts, or after distracted daytime feeds.",
          "Development: new skills (rolling, crawling, pulling to stand) love a 2 a.m. rehearsal.",
        ],
      },
      {
        h2: "What tends to help",
        body: [
          "Start with the whole day, not the loud part of the night. Look at total daytime sleep, the timing of the last nap, and how bedtime actually unfolds. Then look at sleep onset: however your baby falls asleep at 7 p.m. is what they'll call for at 1 a.m.",
          "Change one thing at a time, and give it a few nights before judging it — a single rough night is information, not a verdict.",
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If you've been adjusting naps and bedtime for weeks and the wakings haven't budged, the piece you're missing usually isn't effort — it's someone reading the whole pattern. That's the work I do with families every week.",
        ],
        links: [
          { href: "/baby-sleep-consultant", label: "Baby & infant sleep support (4–17 months)" },
        ],
      },
    ],
    faq: [
      {
        q: "Is waking every two hours ever normal?",
        a: "In the newborn months, yes — frequent feeding is expected. Past about four months, persistent every-two-hours waking usually has a readable cause: schedule, sleep onset, hunger, or development. It's workable.",
      },
      {
        q: "Should I stop feeding at night to fix it?",
        a: "Not as a first move, and not without knowing whether hunger is actually in the mix. Night feeds and sleep associations often overlap — the order and pace of change matter more than the decision itself.",
      },
    ],
    related: [
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/sleep-guides/baby-30-minute-naps", label: "Why your baby only naps for 30 minutes" },
      { href: "/sleep-guides/sleep-regressions", label: "Sleep regressions: what's actually happening" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "baby-30-minute-naps": {
    cluster: "Baby & Infant",
    path: "/sleep-guides/baby-30-minute-naps",
    meta: {
      title: "Why Your Baby Only Naps for 30 Minutes | The Nurtured Parent Co.",
      description:
        "The 30-minute nap, explained: why babies wake after one sleep cycle, when short naps are developmental, and what actually helps naps stretch — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Baby & infant",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "The 30-minute nap" },
    ],
    h1: ["Why your baby only naps", "for 30 minutes."],
    hero: [
      "You finally get them down, exhale, make coffee — and exactly thirty-two minutes later, they're awake. Every nap. Every day. The crib transfer worked; the nap just won't last.",
      "Welcome to the most predictable mystery in infant sleep. Here's what's going on.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "A baby's sleep cycle runs roughly 30–45 minutes. At the end of it, everyone — babies included — surfaces toward waking. Adults roll over and link straight into the next cycle. Your baby surfaces, notices the conditions have changed (or simply doesn't know how to re-enter sleep yet), and the nap is over.",
          "A 30-minute nap is a baby completing one clean cycle and stopping at the junction. The skill they're missing is the link.",
        ],
      },
      {
        h2: "Normal phase vs. stuck pattern",
        body: [
          "Short naps are developmentally normal in the early months — many babies don't reliably link cycles until somewhere around five to seven months, and naps often lengthen on their own as that matures.",
          "It looks more like a pattern worth working on when: every nap is short for weeks on end, your baby wakes clearly still tired (cranky, rubbing eyes, melting down by late afternoon), or nights are fragmenting alongside the short naps.",
        ],
      },
      {
        h2: "What tends to help",
        list: [
          "Check the timing first: a nap that starts too early or too late in the wake window ends short more often.",
          "Watch how they fall asleep — independent sleep onset at nap time is what makes linking possible.",
          "Give the junction a minute: a brief pause before going in sometimes lets a half-woke baby re-link on their own.",
          "Protect the first nap — it's the easiest one to lengthen and sets the day's rhythm.",
          "Keep the room boring: dark, consistent sound, no interesting morning light.",
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If every nap is 30 minutes and every afternoon is a salvage operation, you don't have to wait for maturity to maybe fix it. Naps are one of the most responsive things I work on with families.",
        ],
        links: [
          { href: "/baby-sleep-consultant", label: "Baby & infant sleep support (4–17 months)" },
        ],
      },
    ],
    faq: [
      {
        q: "Do babies grow out of 30-minute naps?",
        a: "Sometimes — as babies mature, they become more capable of linking sleep cycles. But longer naps don't always happen automatically. Babies also need the time, space, and independent sleep skills to practice connecting those cycles on their own.\n\nIf your baby is always rocked, bounced, or fed to sleep, they may look for that same help when they wake between sleep cycles. Helping your baby learn to fall asleep independently gives them the opportunity to practice getting back to sleep without needing the same support that got them there in the first place.\n\nWith consistent opportunities to practice independent sleep, those short 30-minute naps can begin to lengthen and become more predictable and restorative.",
      },
      {
        q: "Should I rescue the nap — rock or feed them back to sleep?",
        a: "As a bridge, occasionally, it's fine — protecting daytime sleep matters. As the permanent mechanism, it usually keeps the pattern in place. The goal is a baby who can link cycles without the rescue.",
      },
    ],
    related: [
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/sleep-guides/baby-waking-every-2-hours", label: "Why your baby is waking every two hours" },
      { href: "/sleep-guides/nap-transitions", label: "Nap transitions: when the schedule shifts" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "baby-wakes-when-put-down": {
    cluster: "Baby & Infant",
    path: "/sleep-guides/baby-wakes-when-put-down",
    meta: {
      title: "Baby Wakes When Put Down? What Helps | The Nurtured Parent Co.",
      description:
        "Why babies wake at the crib transfer, what's normal at each age, and how to make the put-down stop being the moment the night falls apart — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Baby & infant",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Waking at the transfer" },
    ],
    h1: ["Why does my baby wake up", "every time I put them down?"],
    hero: [
      "They're completely out — heavy, warm, limp against your shoulder. You move slowly. You lower them like a sleeping bomb. And the second their back touches the mattress: eyes open, arms up, done.",
      "The crib transfer is where a thousand evenings quietly fall apart. Here's why it happens — and what changes it.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "Two things are working against you at the transfer. The first is physics: your baby is going from warm, moving, upright, and held to flat, still, cool, and alone — their startle reflex and their balance system both register the drop. The second is bigger: they fell asleep in one set of conditions and are surfacing into a different one.",
          "That mismatch is the heart of it. A baby who falls asleep in arms and wakes in the crib isn't being difficult — they're noticing, correctly, that something changed. Your body was the sleep environment: your heartbeat, your warmth, your smell, your motion. The mattress offers none of it.",
        ],
      },
      {
        h2: "Newborns versus older babies",
        body: [
          "In the newborn months, transfer waking is nearly universal — their sleep is light, their startle reflex is loud, and contact is a genuine biological need. It usually eases as sleep matures and the startle settles, often around three to four months. Newborns are far too young for formal sleep training, and they don't need it: this stage responds to patience and technique, not programs.",
          "Past the early months, if every single put-down still fails, the question shifts from the transfer technique to the fall-asleep conditions themselves — because by then, the transfer is often just where a bigger pattern becomes visible.",
        ],
      },
      {
        h2: "The conditions they fell asleep in",
        body: [
          "Babies, like adults, surface between sleep cycles all night — and they check whether the world matches the one they fell asleep in. Rocked to sleep at bedtime means expecting rocking at 1 a.m. The transfer wakes them not because the crib is wrong, but because it's different.",
          "Sleep pressure matters too: a baby put down slightly under-tired startles awake far more easily than one who is truly ready. Feeding timing, a cool sheet, a room that feels unfamiliar — each one is a small vote toward waking.",
        ],
      },
      {
        h2: "Making the transfer kinder",
        body: [
          "Here's what this looks like in a real house. It's 9:40 p.m. You've rocked her for twenty minutes. Instead of lowering her the moment her eyes close, you wait for deep sleep — limp limbs, heavy breathing, no fluttering eyelids. You've already warmed the sheet with a heating pad you removed before she ever touched it. You lower slowly: feet first, then bottom, then head — keeping your body folded over hers so your warmth and smell linger a few seconds longer. Your hand stays firm on her chest for a full minute after the landing. Then you withdraw, gradually.",
          "Does it work every time? No. Does it work far more often than the quick lower-and-pray? Almost always. Practice with the easiest sleep of the day first — usually a morning nap — not the hardest one.",
        ],
        list: [
          "Wait for deep sleep before moving: limp limbs, heavy breathing, no fluttering eyelids.",
          "Warm the sheet first (remove the warming source before baby goes down), and lower slowly — feet, then bottom, then head.",
          "Keep a firm hand on their chest for a minute after the landing; withdraw gradually.",
          "Practice with the easiest sleep of the day, not the hardest one.",
          "Longer term: work toward falling asleep in the crib itself — the transfer problem disappears when there's no transfer.",
        ],
      },
      {
        h2: "A safe, familiar sleep space",
        body: [
          "However the transfer goes, where they land matters most: on their back, on a firm, flat, separate sleep surface — a crib or bassinet with nothing loose in it. No pillows, no loose blankets, no positioners. Room-sharing without bed-sharing keeps them close and keeps them safe.",
          "Consistency helps too. A crib that's only ever visited at 9 p.m. in the dark feels foreign; a crib that's part of everyday life — a few calm minutes awake there in the morning, naps practiced there — feels familiar. Familiar spaces wake babies less.",
        ],
      },
      {
        h2: "When the transfer isn't the real problem",
        body: [
          "If every transfer fails at every age and stage, zoom out. A baby running on short, broken daytime naps arrives at bedtime overtired and harder to settle — which is why fixing days so often fixes nights. A baby fed fully to sleep every time expects the full feed at every waking. And sometimes the honest answer is temperament: one technique transforms one baby and does nothing for another, because the thing driving the waking is different.",
          "That's not failure — it's information. The question stops being 'how do I lower them better' and becomes 'what is sleep actually depending on here?'",
        ],
        links: [
          { href: "/sleep-guides/baby-30-minute-naps", label: "Why your baby only naps for 30 minutes" },
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If your evenings have become a loop of transfer attempts, that's exhausting in a very specific way — and it's very fixable. A personalized plan looks at the whole fall-asleep picture, not just the landing: timing, feeding, environment, and the smallest change that makes the biggest difference for your baby.",
        ],
        links: [
          { href: "/baby-sleep-consultant", label: "Baby & infant sleep support (4–17 months)" },
        ],
      },
    ],
    faq: [
      {
        q: "Why does my baby sleep in my arms but not the crib?",
        a: "Because your arms and the crib are genuinely different sleep environments: warm versus cool, moving versus still, held versus alone — and babies surface between sleep cycles and notice. It's not preference or stubbornness; it's accurate detection of change. The fix is narrowing the difference (deep sleep before transfer, warm sheet, slow lower) and, over time, practicing falling asleep in the crib itself.",
      },
      {
        q: "Why does my baby wake up five minutes after being put down?",
        a: "Five-minute waking usually means one of two things: the transfer happened before deep sleep arrived (the most common cause — wait for limp limbs and heavy breathing), or your baby hit the end of their first light sleep stage and found the conditions changed. Both are technique problems, not baby problems — and both are adjustable.",
      },
      {
        q: "How can I help my baby become comfortable in the crib?",
        a: "Make the crib familiar before making it the finish line: a few content, awake minutes there in the morning, naps practiced there before nights, your hand and voice present while they settle. Comfort in a space is learned through good experiences in it — which is why the practice starts with easy wins, not midnight battles.",
      },
      {
        q: "Does this happen during naps and nighttime sleep?",
        a: "Both — the mechanism is identical. Naps are actually the better place to practice: stakes are lower, you're not exhausted, and a failed transfer costs twenty minutes instead of your whole night. Skills built at naptime transfer to bedtime remarkably well.",
      },
      {
        q: "At what age does this usually improve?",
        a: "Transfer waking is nearly universal in newborns and typically eases around three to four months as the startle reflex settles and sleep cycles mature. If it's still defining every evening well past that window, the transfer technique usually isn't the issue — the fall-asleep pattern is, and that's very workable with a plan.",
      },
      {
        q: "Is it okay to hold them for sleeps in the meantime?",
        a: "For naps while you build crib skills, absolutely — protecting sleep matters. For nights, safety leads: a firm, flat, empty crib or bassinet is the safe space, so keep overnight sleep there even when it's harder.",
      },
    ],
    related: [
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/sleep-guides/newborn-only-sleeps-on-me", label: "Why your newborn only sleeps on you" },
      { href: "/sleep-guides/baby-30-minute-naps", label: "Why your baby only naps for 30 minutes" },
      { href: "/sleep-guides/baby-waking-every-2-hours", label: "Why your baby is waking every two hours" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "nap-transitions": {
    cluster: "Baby & Infant",
    path: "/sleep-guides/nap-transitions",
    meta: {
      title: "Nap Transitions: Dropping Naps Without the Chaos | The Nurtured Parent Co.",
      description:
        "3-to-2, 2-to-1: how to tell a real nap transition from a rough week, and how to move through it without losing nights — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Baby & infant",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Nap transitions" },
    ],
    h1: ["Nap transitions: when the", "whole schedule shifts."],
    hero: [
      "The schedule finally worked. You knew exactly when naps happened, when bedtime landed, what the day looked like. And then, seemingly overnight, the third nap became a fight, bedtime drifted, and the whole structure started wobbling.",
      "That's usually not a regression. It's a transition — your child's sleep needs are changing underneath the schedule you built.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "Babies drop naps on a fairly predictable arc: from many naps down to three, three to two (often somewhere around seven to nine months), two to one (often somewhere around fifteen to eighteen months). Each drop is the same story: wake windows lengthen, the last nap stops fitting, and the day needs reorganizing.",
          "The wobble is real but temporary. The mistake is usually not the transition — it's changing five things at once in response to it.",
        ],
      },
      {
        h2: "Real transition vs. rough week",
        list: [
          "The refused nap keeps happening — not twice, but most days for a week or more.",
          "When they skip it, they make it to bedtime in reasonable shape.",
          "Bedtime is drifting later, or night sleep is shortening.",
          "The remaining naps are solid — it's the schedule edges that are failing, not sleep itself.",
        ],
      },
      {
        h2: "How to move through it",
        body: [
          "Shift gradually: stretch wake windows by small amounts rather than deleting a nap overnight. Use an earlier bedtime as the pressure valve during the transition weeks — it prevents the overtired spiral that makes everything look worse than it is.",
          "And expect a messy middle: some days on the old schedule, some on the new. That's the transition working, not failing.",
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If the transition has been 'happening' for a month and nothing has settled, there's usually a specific snag — timing, bedtime, or how nights are interacting with days. A short look at the whole picture usually finds it.",
        ],
        links: [
          { href: "/baby-sleep-consultant", label: "Baby & infant sleep support (4–17 months)" },
        ],
      },
    ],
    faq: [
      {
        q: "How long does a nap transition take?",
        a: "Usually one to three weeks of wobble before the new rhythm holds. Longer than that usually means something specific is snagging it — which is findable.",
      },
      {
        q: "Should I drop the nap cold turkey?",
        a: "Rarely. Gradual shifts with an earlier bedtime as the safety valve get you there with far less overtiredness. Cold turkey works for some toddlers at the 2-to-1, but even then, early bedtime does the heavy lifting.",
      },
    ],
    related: [
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/sleep-guides/baby-30-minute-naps", label: "Why your baby only naps for 30 minutes" },
      { href: "/sleep-guides/sleep-regressions", label: "Sleep regressions: what's actually happening" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "sleep-regressions": {
    cluster: "Baby & Infant",
    path: "/sleep-guides/sleep-regressions",
    meta: {
      title: "Sleep Regressions: What's Actually Happening | The Nurtured Parent Co.",
      description:
        "The 4-month regression and friends: what sleep regressions really are, which ones are developmental, and how to come out the other side — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Baby & infant",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Sleep regressions" },
    ],
    h1: ["Sleep regressions:", "what's actually happening."],
    hero: [
      "Sleep was working. Maybe not perfectly, but working — and then, in the space of a week, it wasn't. Wakings doubled. Naps shrank. Someone online told you it's the regression, and it'll pass. Someone else told you it never passes on its own.",
      "Here's the calmer truth about regressions.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "The famous '4-month regression' isn't a regression at all — it's a permanent maturation. Baby sleep reorganizes into adult-like cycles, which means more junctions in the night to surface at. Babies who could already link cycles sail through it; babies who relied on being fully helped to sleep start waking at every junction.",
          "Other regressions cluster around development: rolling, crawling, standing, language bursts, separation awareness. The skill rehearses at night; sleep temporarily frays.",
        ],
      },
      {
        h2: "Which ones pass — and which linger",
        list: [
          "Developmental bursts usually settle in one to three weeks once the skill is mastered.",
          "The 4-month maturation doesn't reverse — but sleep can absolutely be rebuilt on the new wiring.",
          "A 'regression' that runs six-plus weeks usually isn't a regression anymore — it's a new pattern that formed during one.",
        ],
      },
      {
        h2: "How to come out the other side",
        body: [
          "During a developmental burst: keep the structure, offer the extra comfort, practice the new skill during the day, and wait for the wave to pass. What you don't want is five new habits installed during a two-week storm.",
          "After the 4-month shift: the work is sleep onset — helping your baby learn to fall asleep (and link cycles) with progressively less help, at a pace your family can actually hold.",
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If the wave never passed — if you're six weeks past 'it should settle any day now' — that's the moment. We'll figure out what's developmental and what's become structural, and rebuild from there.",
        ],
        links: [
          { href: "/baby-sleep-consultant", label: "Baby & infant sleep support (4–17 months)" },
        ],
      },
    ],
    faq: [
      {
        q: "Do sleep regressions happen at specific ages?",
        a: "The honest answer: development doesn't follow a calendar as tightly as the internet says. The 4-month maturation is real and physiological; beyond that, 'regressions' tend to track skills and changes more than birthdays.",
      },
      {
        q: "Should I sleep train during a regression?",
        a: "Mid-storm is rarely the moment to start something new. Stabilize first; then, if the underlying pattern needs work, choose a settled week and a method that fits your family. What that looks like is exactly what we can figure out together.",
      },
    ],
    related: [
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/sleep-guides/baby-waking-every-2-hours", label: "Why your baby is waking every two hours" },
      { href: "/sleep-guides/nap-transitions", label: "Nap transitions: when the schedule shifts" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "toddler-fighting-bedtime": {
    cluster: "Toddler",
    path: "/sleep-guides/toddler-fighting-bedtime",
    meta: {
      title: "Toddler Fighting Bedtime? Causes & Solutions | The Nurtured Parent Co.",
      description:
        "Toddler bedtime battles — the stalling, the negotiations, the hallway encores. What's driving the resistance and what actually ends it — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Toddler",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Toddler bedtime battles" },
    ],
    h1: ["Why is my toddler fighting bedtime?", "What parents can do."],
    hero: [
      "One more book. One more song. Water. A different blanket. The potty, suddenly, urgently. An hour after you started, you're still in the hallway — and your toddler has somehow negotiated bedtime into a part-time job.",
      "Toddler bedtime resistance looks like defiance. It usually isn't. Here's what's underneath it — and what actually ends it.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "Toddlers live two driving forces at once: a powerful new need for autonomy, and a brain that genuinely cannot hold limits without your help. Bedtime is where those collide — it's the moment they have the least control and the most to say about it.",
          "Add the common accelerants: a bedtime that's drifted past their real sleep window, a routine with soft edges, or a pattern where protesting has reliably produced more time, more contact, or a different outcome.",
        ],
      },
      {
        h2: "Overtired, undertired, or simply not done with the day",
        body: [
          "Three different problems wear the same costume, and the fix for each is different. An overtired toddler is wired — a second wind, silly then suddenly melting down, arching against the whole idea of bed. An undertired toddler is simply not sleepy: cheerful, playful, genuinely confused about why the day is ending. And a toddler with true bedtime resistance is often tired enough but unwilling — the issue is the separation or the limit, not the sleep pressure.",
          "Naps sit right in the middle of this. A nap that runs too late or too long pushes real sleepiness past bedtime — the classic undertired standoff at 9 p.m. A dropped nap that happened before they were ready produces the wired, overtired version. Before changing anything else, look at when the nap happened and when bedtime actually is.",
        ],
      },
      {
        h2: "Separation anxiety is real at this age",
        body: [
          "Somewhere in the toddler years, object permanence meets imagination: your child now knows you exist when you're gone — and can picture it. The door closing becomes the hard part, not the bed. Developmental leaps, new fears, a new sibling, a moved room: all of it can surface at bedtime, the day's one long separation.",
          "A toddler who keeps appearing in the hallway is often running a very simple check: are you still there? The answer they need is yes — delivered calmly, briefly, and from the same script every time.",
        ],
      },
      {
        h2: "When they keep getting out of bed",
        body: [
          "The pattern that works is unglamorous: the calm, boring return. First appearance — a hug, a reminder, walk them back. Tenth appearance — the same words, the same walk, minus the hug. No lectures, no frustration performances, no new negotiations at 8:45 p.m. What ends the hallway encore isn't a cleverer consequence; it's the discovery that getting up reliably produces nothing interesting.",
          "Here's what this sounds like in a real hallway: 'It's sleep time. I love you. I'll see you in the morning.' Every time. The first nights are the hardest — some toddlers test the system thoroughly before trusting it. The ones who find the edge never moves stop testing it.",
        ],
      },
      {
        h2: "A routine that actually ends",
        body: [
          "A good toddler bedtime routine is boring, beautiful, and repeatable: the same steps, in the same order, in about twenty to thirty minutes. Bath, pajamas, teeth, two books, song, light out — whatever your version is, its power comes from sameness. Within it, offer choices that don't change the outcome: this book or that one, the blue blanket or the yellow one. Choices inside the limit give autonomy; the limit itself stays yours.",
          "And connection before correction: ten minutes of genuine, undivided attention during the routine does more than an hour of hallway management. A toddler who got you — really got you — before the door closes has less reason to reopen it.",
        ],
      },
      {
        h2: "What quietly keeps the battle going",
        body: [
          "The well-meant moves that accidentally extend bedtime: limits that shift night to night (toddlers test what sometimes moves), big emotional reactions to stalling (big reactions are interesting), renegotiating after 'last book' has been declared, starting the routine before anyone is actually tired, and a wind-down that has quietly grown forty extra steps.",
          "None of these make you a bad parent — they make you a normal one at 8:30 at night. They're just the levers to look at first.",
        ],
      },
      {
        h2: "When bedtime takes an hour or more",
        body: [
          "Audit before you intervene. Is bedtime matched to when they're actually sleepy, or to when the clock says? Has the routine quietly doubled? Is the nap schedule pushing sleepiness too late? Most hour-long bedtimes are a timing problem wearing a behavior costume.",
          "Then pick one plan — the routine, the limits, the response to getting up — and hold it identically for two weeks. Consistency across nights is the whole mechanism: a toddler who's learning where the edge is will test it; a toddler who finds it never moves, stops.",
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If bedtime has become the part of the day you dread — if it's an hour-plus, every night, and everyone's frayed — that's exactly the situation toddler support is built for. This changes faster than you think.",
        ],
        links: [
          { href: "/toddler-sleep-consultant", label: "Toddler sleep support (18+ months)" },
        ],
      },
    ],
    faq: [
      {
        q: "Why does my 2-year-old suddenly refuse bedtime?",
        a: "Because two is peak autonomy meeting peak separation awareness. Your toddler has discovered they have a say in things — and bedtime is the moment they have the least of one. Add a nap shift, a developmental leap, or a routine that drifted, and a previously easy bedtime can turn. 'Suddenly' almost always has a findable cause, and the cause points to the fix.",
      },
      {
        q: "Why does my toddler cry when I leave the room?",
        a: "Usually separation, not fear of the bed itself. Toddlers know you exist when the door closes — that's exactly the problem. A predictable goodnight script, a door left open an agreed amount, and calm, brief check-ins on a schedule you decide (not one they negotiate) reassure without turning leaving into a performance.",
      },
      {
        q: "How do I stop bedtime battles?",
        a: "Three levers, in order: timing (bedtime matched to real sleepiness, nap not running too late), routine (same steps, same order, twenty to thirty minutes, connection built in), and response (limits held warmly and identically every night, with boring returns for hallway appearances). Most battles are one of those three quietly off — fix the right one and the battle deflates within days to weeks.",
      },
      {
        q: "What if my toddler gets out of bed repeatedly?",
        a: "Return them calmly, briefly, and boringly — every single time, with the same short script and no new conversation. The first nights may involve many returns; that's normal and it drops fast once the pattern proves uninteresting. If fear seems to be driving it (not just testing), address the fear in daylight, not at 9 p.m.",
      },
      {
        q: "How long should a toddler bedtime routine take?",
        a: "Twenty to thirty minutes is the sweet spot for most toddlers — long enough to genuinely wind down, short enough that it can't balloon into negotiation territory. If yours regularly runs an hour, the routine has probably grown steps over time; trimming it back to a fixed, predictable sequence usually helps more than adding anything.",
      },
      {
        q: "Is it a phase or a pattern?",
        a: "A phase has a shape — a start, a peak, an end, usually within a couple of weeks. If the battle has been nightly for a month or more, it's a pattern — and patterns respond to a plan.",
      },
    ],
    related: [
      { href: "/toddler-sleep-consultant", label: "Toddler sleep consultant" },
      { href: "/sleep-guides/early-morning-waking", label: "Early morning waking: when 5 a.m. becomes the norm" },
      { href: "/sleep-guides/sleep-regressions", label: "Sleep regressions: what's actually happening" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "early-morning-waking": {
    cluster: "Toddler",
    path: "/sleep-guides/early-morning-waking",
    meta: {
      title: "Early Morning Waking: When 5 A.M. Becomes the Norm | The Nurtured Parent Co.",
      description:
        "Why babies and toddlers start waking at 5 a.m., why it's the hardest waking to fix, and what actually shifts it — from a certified pediatric sleep consultant.",
    },
    label: "Sleep guides · Toddler",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Early morning waking" },
    ],
    h1: ["Early morning waking:", "when 5 a.m. becomes the norm."],
    hero: [
      "The night actually goes fine now — and then 5:04 a.m. arrives like a daily appointment. Your child is up, bright, completely done with sleep, and your day begins in the dark.",
      "Early waking is famously the last piece of sleep to fall into place. Here's why — and what moves it.",
    ],
    sections: [
      {
        h2: "What's actually happening",
        body: [
          "The last stretch of night sleep is the lightest and most fragile. By 4–6 a.m., sleep pressure is nearly spent, melatonin is fading, and the environment is changing — birds, light, the house stirring. Waking then is easy. The question is why they can't re-enter sleep.",
          "The honest answer is usually a combination: a schedule imbalance (too much or poorly timed daytime sleep), a bedtime slightly off their rhythm, light leaking into the room — or a habit loop where 5 a.m. has quietly become morning, complete with response.",
        ],
      },
      {
        h2: "Why it's the hardest one",
        body: [
          "Because there's no sleep pressure left to work with. At 2 a.m., sleep pulls them back under; at 5 a.m., it barely does. That's why willpower approaches fail here — and why early waking responds to schedule and environment changes more than to any middle-of-the-night technique.",
        ],
      },
      {
        h2: "What tends to shift it",
        list: [
          "Audit the light first: true darkness until your target morning time matters more than almost anything.",
          "Check total daytime sleep and the last wake window — both directions of imbalance show up at dawn.",
          "Hold a consistent 'morning starts at' response: low-key, dim, boring before the target time.",
          "Watch what 5 a.m. produces — if it reliably produces fun, food, or parents, it will keep happening.",
          "Protect bedtime from drift; an over-late bedtime often makes early waking worse, not better.",
        ],
      },
      {
        h2: "When it's worth a conversation",
        body: [
          "If you've blacked out the room, held the line, adjusted naps, and 5 a.m. is still the norm — there's a specific imbalance somewhere, and it's findable. Early waking is one of my favorite problems to solve, precisely because it looks unsolvable from the inside.",
        ],
        links: [
          { href: "/toddler-sleep-consultant", label: "Toddler sleep support (18+ months)" },
        ],
      },
    ],
    faq: [
      {
        q: "Is 5 a.m. just their natural wake time?",
        a: "Sometimes a genuinely early rhythm exists — but far more often, something in the schedule or environment is holding it there. Worth a real look before accepting it as permanent.",
      },
      {
        q: "Will an earlier bedtime make them wake even earlier?",
        a: "Counterintuitively, no — for most overtired kids, an earlier bedtime protects the last stretch of the night. Overtiredness is one of the most common drivers of early waking.",
      },
    ],
    related: [
      { href: "/toddler-sleep-consultant", label: "Toddler sleep consultant" },
      { href: "/sleep-guides/toddler-fighting-bedtime", label: "When your toddler fights bedtime" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "when-to-hire-sleep-consultant": {
    cluster: "Working with a consultant",
    path: "/sleep-guides/when-to-hire-sleep-consultant",
    meta: {
      title: "When to Hire a Pediatric Sleep Consultant | The Nurtured Parent Co.",
      description:
        "There's no 'bad enough' threshold. The honest signs it's time to bring in a pediatric sleep consultant — and what actually changes when you do.",
    },
    label: "Sleep guides · Working together",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "When to hire a consultant" },
    ],
    h1: ["When to hire a", "pediatric sleep consultant."],
    hero: [
      "Most families don't call at the beginning of the hard part. They call after weeks — sometimes months — of trying: the schedules, the wake windows, the method from the book, the thing that worked for their friend's baby.",
      "If you're wondering whether it's 'bad enough' to justify help, this page is for you.",
    ],
    sections: [
      {
        h2: "The honest signs",
        list: [
          "You've been adjusting naps and bedtime for weeks, and nothing is sticking.",
          "You're no longer sure which advice even applies to your child anymore.",
          "Nights are affecting days — your patience, your partner, your work, your joy.",
          "You've started dreading bedtime before it arrives.",
          "Everyone you ask has a different answer, and none of them know your child.",
          "You're simply too tired to keep being the researcher.",
        ],
      },
      {
        h2: "What actually changes when you hire one",
        body: [
          "The searching stops. Instead of fifteen open tabs, you get one person who has looked at your child's whole picture — age, temperament, routines, what's been tried — and can tell you which part of the pattern matters.",
          "And the implementation stops being lonely. When the plan meets a real Thursday, you have someone to ask — someone who already knows your situation — instead of starting over at a search engine.",
        ],
      },
      {
        h2: "What it isn't",
        body: [
          "It isn't an admission of failure. It isn't a commitment to a method you hate. It isn't only for crisis-level sleep. Most of the families I work with are thoughtful, loving parents who simply reached the end of guessing — which is exactly the right time to bring in a second set of trained eyes.",
        ],
      },
      {
        h2: "The lowest-pressure way to find out",
        body: [
          "The complimentary pediatric sleep assessment exists for exactly this question. We talk through what's happening, I'll tell you honestly what I'd look at first and whether support makes sense — and then you decide.",
        ],
        links: [
          { href: "/pediatric-sleep-consultant", label: "What a pediatric sleep consultant does" },
        ],
      },
    ],
    faq: [
      {
        q: "Is my situation 'bad enough' to justify a consultant?",
        a: "There's no threshold. If sleep is costing your family more than it should and your own efforts have stopped moving it, that's sufficient. Most parents tell me they wish they'd asked sooner.",
      },
      {
        q: "Can we just try one more thing on our own first?",
        a: "Of course — and if it works, genuinely, that's the best outcome. The sign to stop self-experimenting is when each new attempt is harder to evaluate than the last. That's when outside perspective pays for itself.",
      },
    ],
    related: [
      { href: "/pediatric-sleep-consultant", label: "Pediatric sleep consultant" },
      { href: "/sleep-guides/sleep-consultant-vs-sleep-coach", label: "Sleep consultant vs. sleep coach" },
      { href: "/sleep-guides/how-virtual-sleep-consulting-works", label: "How virtual sleep consulting works" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "sleep-consultant-vs-sleep-coach": {
    cluster: "Working with a consultant",
    path: "/sleep-guides/sleep-consultant-vs-sleep-coach",
    meta: {
      title: "Sleep Consultant vs. Sleep Coach: What's the Difference? | The Nurtured Parent Co.",
      description:
        "Baby sleep consultant, infant sleep coach, pediatric sleep consultant — what the titles actually mean, what matters more than the label, and how to choose.",
    },
    label: "Sleep guides · Working together",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Consultant vs. coach" },
    ],
    h1: ["Sleep consultant vs. sleep coach:", "what's the difference?"],
    hero: [
      "Search for help with your baby's sleep and you'll meet a wall of titles: sleep consultant, sleep coach, sleep trainer, sleep specialist — baby, infant, pediatric versions of each. It looks like a menu of meaningfully different things.",
      "Mostly, it isn't. Here's an honest decoder.",
    ],
    sections: [
      {
        h2: "What the titles actually mean",
        body: [
          "There is no licensing body behind these titles. 'Consultant,' 'coach,' and 'specialist' are used interchangeably across the industry — the same person might appear under all three in different search results. 'Pediatric' typically signals work with children rather than adults; 'baby' or 'infant' signals the stage.",
          "So if the title doesn't tell you much, what should you actually look at?",
        ],
      },
      {
        h2: "What matters more than the label",
        list: [
          "Training and certification: a real program with real curriculum (mine is the Center for Pediatric Sleep Management).",
          "Personalization: do they assess your child first, or sell one method to everyone?",
          "Implementation support: do they stay with you while you apply the plan, or hand over a PDF and disappear?",
          "Scope honesty: do they know what they're not? Medical questions belong to your pediatrician — a good consultant says so plainly.",
          "Fit: you'll be talking to this person on your hardest weeks. The conversation should feel like relief, not pressure.",
        ],
      },
      {
        h2: "And what to be careful of",
        body: [
          "Be cautious of anyone promising a specific night-by-night outcome, selling one branded method as the answer to everything, or making medical-sounding claims. Sleep work is real, skilled, and hugely effective — but it happens inside your actual family, not a sales page.",
        ],
      },
      {
        h2: "How I work, whatever you call it",
        body: [
          "I'm a certified pediatric sleep consultant — but what I'd want you to notice is the shape of the work: understand first, plan around your child, stay beside you while it happens. If that sounds like what you've been looking for, the complimentary assessment is where we start.",
        ],
        links: [
          { href: "/pediatric-sleep-consultant", label: "What a pediatric sleep consultant does" },
        ],
      },
    ],
    faq: [
      {
        q: "Is a sleep consultant the same as a sleep trainer?",
        a: "Sleep training is one tool inside the work, not the work itself. Much of what I do — especially with newborns — isn't conventional sleep training at all: it's foundations, schedules, environment, and understanding what's actually driving the struggle.",
      },
      {
        q: "Do I need someone local for this to work?",
        a: "No. Sleep happens at your home, and virtual support works in your real environment — most of the families I work with are virtual, nationwide. (Denver-area families can also ask about in-home support.)",
      },
    ],
    related: [
      { href: "/pediatric-sleep-consultant", label: "Pediatric sleep consultant" },
      { href: "/sleep-guides/when-to-hire-sleep-consultant", label: "When to hire a sleep consultant" },
      { href: "/virtual-sleep-consultant", label: "Virtual sleep consultant" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },

  "how-virtual-sleep-consulting-works": {
    cluster: "Working with a consultant",
    path: "/sleep-guides/how-virtual-sleep-consulting-works",
    meta: {
      title: "How Virtual Pediatric Sleep Consulting Works | The Nurtured Parent Co.",
      description:
        "No video tours of your nursery required. How virtual pediatric sleep consulting actually works — assessment, plan, and real-time support while you implement, wherever you live.",
    },
    label: "Sleep guides · Working together",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "How virtual consulting works" },
    ],
    h1: ["How virtual pediatric sleep", "consulting actually works."],
    hero: [
      "Parents sometimes picture virtual sleep consulting as a video call where they hold up a phone to the nursery while a stranger squints at the crib. It isn't that — and honestly, it doesn't need to be.",
      "Sleep consulting happens in conversation, pattern-reading, and support. All of that travels beautifully. Here's the real shape of it.",
    ],
    sections: [
      {
        h2: "Why virtual works so naturally for sleep",
        body: [
          "Sleep happens at home — your home, your nursery, your 2 a.m. hallway. The work of sleep consulting isn't observing a single bedtime on camera; it's understanding the whole pattern: how nights go, how days go, how your child falls asleep, what happens when they wake.",
          "That understanding comes from talking — carefully, with someone who knows which details matter. And that kind of talking works exactly the same whether we're across a table or across the country.",
        ],
      },
      {
        h2: "What it actually looks like",
        list: [
          "A complimentary pediatric sleep assessment: you tell me what's happening, I ask the questions that narrow it down.",
          "A detailed intake: routines, environment, temperament, history — the whole picture, in writing, at your pace.",
          "A plan built around your child: what we'll change, in what order, and why.",
          "Real-time support while you implement: you report what happened; we adjust together.",
        ],
      },
      {
        h2: "The part people don't expect",
        body: [
          "Most families tell me the implementation support is the part that mattered most — not the plan itself. When a nap goes sideways on day three, you don't reinterpret the internet alone. You tell me what happened, and since I already know your child and your plan, the answer is specific: hold this, adjust that, tonight gets this.",
          "That's the piece a book can't give you, and it needs no travel at all.",
        ],
      },
      {
        h2: "Wherever you live",
        body: [
          "I work with families across the United States this way — and Denver-area families can additionally ask about in-home support. Either way, it starts with the same complimentary conversation.",
        ],
        links: [
          { href: "/virtual-sleep-consultant", label: "Virtual sleep consultant — the full picture" },
          { href: "/denver-in-home-sleep-consultant", label: "In-home support (Denver area)" },
        ],
      },
    ],
    faq: [
      {
        q: "Do you need to see my baby sleep on video?",
        a: "No. What I need is the pattern — and that comes from talking with you and from your intake details. If a specific moment ever needs a closer look, there are simple ways to share it, but video observation isn't the mechanism.",
      },
      {
        q: "Is virtual support as effective as in-home?",
        a: "For the great majority of families, yes — the conversation, the plan, and the implementation support are the service, and they work identically. In-home adds environmental context for the situations that genuinely need it.",
      },
    ],
    related: [
      { href: "/virtual-sleep-consultant", label: "Virtual sleep consultant" },
      { href: "/sleep-guides/when-to-hire-sleep-consultant", label: "When to hire a sleep consultant" },
      { href: "/pediatric-sleep-consultant", label: "Pediatric sleep consultant" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },
  // PUBLISHED 2026-10-12 (owner approved). Written WITHOUT aggregate
  // statistics per the research-article safeguard: the structured assessment
  // data was insufficient for publishable numbers, so the article covers
  // Andrea's verified approach only.
  "what-i-look-for-baby-sleep-assessment": {
    cluster: "Working with a consultant",
    path: "/sleep-guides/what-i-look-for-baby-sleep-assessment",
    meta: {
      title: "What I Look for in a Baby Sleep Assessment | The Nurtured Parent Co.",
      description:
        "Before I suggest a single change, I ask questions. What a pediatric sleep consultant actually looks at — routines, nights, naps, development, and what you've already tried.",
    },
    label: "Sleep guides · Working with a consultant",
    crumbs: [
      { label: "Home", href: "/" },
      { label: "Sleep Guides", href: "/sleep-guides" },
      { label: "Inside a sleep assessment" },
    ],
    h1: ["What I look for in", "a baby sleep assessment."],
    hero: [
      "A baby waking every two hours. Naps that never seem to last. A toddler who suddenly refuses bedtime. These are the problems that bring parents looking for a pediatric sleep consultant — but two families can describe similar nights and be dealing with very different circumstances.",
      "That's why I start by asking questions before recommending changes. What happened before bedtime? How did your child fall asleep? What happens when they wake? And what have you already tried? I want to understand the whole picture, not just the hardest part of the night.",
    ],
    sections: [
      {
        h2: "Why the questions come first",
        body: [
          "Most sleep advice starts with the problem and jumps to a fix. In my experience that's backwards. 'Waking every two hours' can be hunger in one house, a nap schedule in another, and a fall-asleep pattern in a third — and the right response is different in each.",
          "So before we talk about changing anything, I want to know how sleep actually works in your home. Not the version the books describe — the 7:40 p.m. real one.",
        ],
      },
      {
        h2: "How your child falls asleep",
        body: [
          "This is one of the most informative things I learn — though never the whole story on its own. Babies and toddlers, like adults, surface between sleep cycles all night — and they check whether the world matches the one they fell asleep in. Rocked, fed, or held to sleep at bedtime often means expecting the same at 1 a.m.",
          "None of that is a mistake. Feeding to sleep is not a failure, and contact is not a problem to be ashamed of. It's simply information — one important consideration that sits alongside naps, the daily rhythm, your child's developmental stage, and what you've already tried. Each one reshapes what the others mean.",
        ],
      },
      {
        h2: "The shape of the nights",
        body: [
          "When the wakings happen matters as much as how many there are. A waking forty-five minutes after bedtime is a different story than a 2 a.m. stretch, and a 5 a.m. wake-up is its own puzzle entirely — the lightest sleep of the night, the hardest to fix with the same tool as the others.",
          "I also listen for what the waking looks like: settles with a hand on the chest, needs full rocking, wide awake and chatty, or upset in a way that feels different. Each detail narrows the picture.",
        ],
      },
      {
        h2: "Naps and the rhythm of the day",
        body: [
          "Days and nights are one system. A nap that runs too late pushes real sleepiness past bedtime; a dropped nap that came too early produces the wired, overtired evening; short naps all day can mean a baby arrives at night running on empty.",
          "So I always ask about the daytime before I touch the nighttime. Very often the night's answer starts at 2 p.m.",
        ],
      },
      {
        h2: "The developmental moment",
        body: [
          "What's normal at six weeks would be a red flag at sixteen months, and what works for a newborn isn't right for a toddler. A four-month-old waking twice to feed is often just being four months old. A two-year-old's sudden bedtime refusal is usually autonomy, not regression.",
          "Age-appropriate expectations are the quiet foundation of everything I recommend — including the recommendation to wait, watch, and change nothing yet, when that's the honest answer.",
        ],
      },
      {
        h2: "What you've already tried",
        body: [
          "By the time parents reach me, most have tried a lot — schedules, earlier bedtimes, later bedtimes, white noise, darker rooms, advice from three different corners of the internet. What you've tried tells me two things: what doesn't fit your child, and what you're comfortable with.",
          "Your comfort matters more than any method. A plan you can't carry out at 3 a.m. isn't a plan.",
        ],
      },
      {
        h2: "What this means for your family",
        body: [
          "If you've read the generic schedules and wondered why none of them quite fit — it's because they're written for an average child who doesn't exist. Your child's sleep makes sense once the whole picture is on the table: the routine, the nights, the days, the age, and the history.",
          "Understanding that picture is exactly what the free sleep assessment is for. It's a conversation, not a commitment — a chance to explore your concerns together and see what possible next steps could look like for your family.",
        ],
      },
    ],
    faq: [
      {
        q: "What actually happens in a free sleep assessment?",
        a: "A relaxed conversation about your child's sleep — what's happening, what you've tried, and what you're hoping for. I'll share what I'm hearing and what I'd want to understand better, and we'll explore possible next steps. If working together makes sense, I'll explain how.",
      },
      {
        q: "Do I need to track sleep or prepare anything beforehand?",
        a: "Nothing formal. It helps to have thought about two things before we talk: what's feeling hardest right now, and what you've already tried. Beyond that, just bring the honest picture of your nights — tired brains are allowed.",
      },
      {
        q: "Is the free assessment a sales call in disguise?",
        a: "No. Every family's sleep situation is different, and whether I can genuinely help depends on understanding your circumstances first. The assessment is how we figure that out together — how I learn whether I'm the right help, and how you decide whether you want it.",
      },
      {
        q: "When is it a pediatrician question instead of a sleep one?",
        a: "Anything that smells medical goes to your pediatrician first — discomfort that seems physical, feeding concerns, breathing sounds, reflux that isn't managed. Sleep consulting works alongside medical care, never instead of it.",
      },
    ],
    related: [
      { href: "/baby-sleep-consultant", label: "Baby sleep consultant" },
      { href: "/newborn-sleep-consultant", label: "Newborn sleep consultant" },
      { href: "/toddler-sleep-consultant", label: "Toddler sleep consultant" },
      { href: "/sleep-guides", label: "All sleep guides" },
    ],
  },
};
export const GUIDE_CLUSTERS = ["Newborn", "Baby & Infant", "Toddler", "Working with a consultant"];

export const GUIDE_DESCRIPTIONS = {
  "newborn-day-night-confusion": "Days and nights mixed up — what's happening and what gently helps.",
  "newborn-only-sleeps-on-me": "Contact sleep and the fourth trimester — normal, and workable.",
  "baby-waking-every-2-hours": "Reading the pattern behind the every-two-hours night.",
  "baby-30-minute-naps": "One clean sleep cycle, then awake — why, and what stretches it.",
  "baby-wakes-when-put-down": "The crib transfer — why the landing wakes them.",
  "nap-transitions": "Dropping naps without losing the nights.",
  "sleep-regressions": "What's developmental, what's structural, what to do.",
  "toddler-fighting-bedtime": "The stalling, the negotiations, the hallway encore.",
  "early-morning-waking": "Why 5 a.m. is the hardest waking — and what shifts it.",
  "when-to-hire-sleep-consultant": "The honest signs it's time to bring in help.",
  "sleep-consultant-vs-sleep-coach": "Titles, decoded — and what actually matters.",
  "how-virtual-sleep-consulting-works": "No nursery video calls. How it really works.",
  "what-i-look-for-baby-sleep-assessment": "Why I ask questions before suggesting a single change.",
};
